import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { UserPlus, KeyRound, Trash2, Pencil, Archive, ArchiveRestore, RotateCcw, FileText } from "lucide-react";

const ROLE_LABEL = { admin: "Admin", chef: "Chef d'atelier", student: "Étudiant" };

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState("active"); // active | archived | deleted
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pwdUser, setPwdUser] = useState(null);
  const [newPwd, setNewPwd] = useState("");

  const isAdmin = me?.role === "admin";
  const canManageStudents = me?.role === "admin" || me?.role === "chef";

  const load = (t = tab) => api.get(`/users?status=${t}`).then((r) => setUsers(r.data));
  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  const downloadProfilePdf = async (u) => {
    try {
      const resp = await api.get(`/users/${u.id}/profile-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      const safe = (u.name || u.id).replace(/\s+/g, "_").replace(/[/\\]/g, "-");
      a.href = url;
      a.download = `fiche-${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Fiche PDF téléchargée`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openCreate = () => {
    setEditing({
      first_name: "", last_name: "", email: "", phone: "", role: "student", quota_year_hours: "",
      address_street: "", address_postal_code: "", address_city: "", address_country: "",
      iban: "", date_of_birth: "", national_register: "",
      emergency_contact_name: "", emergency_contact_phone: "", emergency_contact_relation: "",
    });
    setDialogOpen(true);
  };
  const openEdit = (u) => {
    setEditing({
      ...u,
      first_name: u.first_name || (u.name ? u.name.split(" ")[0] : ""),
      last_name: u.last_name || (u.name ? u.name.split(" ").slice(1).join(" ") : ""),
    });
    setDialogOpen(true);
  };

  const save = async () => {
    try {
      const full_name = `${editing.first_name || ""} ${editing.last_name || ""}`.trim() || editing.name || "";
      const extendedFields = {
        address_street: editing.address_street || null,
        address_postal_code: editing.address_postal_code || null,
        address_city: editing.address_city || null,
        address_country: editing.address_country || null,
        iban: editing.iban || null,
        date_of_birth: editing.date_of_birth || null,
        national_register: editing.national_register || null,
        emergency_contact_name: editing.emergency_contact_name || null,
        emergency_contact_phone: editing.emergency_contact_phone || null,
        emergency_contact_relation: editing.emergency_contact_relation || null,
      };
      if (editing.id) {
        await api.patch(`/users/${editing.id}`, {
          name: full_name,
          first_name: editing.first_name || null,
          last_name: editing.last_name || null,
          phone: editing.phone || null,
          email: editing.email,
          role: editing.role,
          active: editing.active,
          quota_year_hours: editing.quota_year_hours ? Number(editing.quota_year_hours) : null,
          ...extendedFields,
        });
        toast.success("Compte mis à jour");
      } else {
        const { data } = await api.post("/users", {
          name: full_name,
          first_name: editing.first_name || null,
          last_name: editing.last_name || null,
          phone: editing.phone || null,
          email: editing.email,
          role: editing.role,
          quota_year_hours: editing.quota_year_hours ? Number(editing.quota_year_hours) : null,
          ...extendedFields,
        });
        if (data.initial_password) {
          toast.success(`Compte créé — mdp initial : ${data.initial_password} (envoyé par email)`, { duration: 12000 });
        } else {
          toast.success("Compte créé");
        }
      }
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(
      `Mettre ${u.name} à la corbeille ?\n\nLe compte sera désactivé immédiatement mais ses données seront conservées 30 jours, le temps que tu puisses le restaurer si besoin. Au-delà, suppression définitive automatique.`
    )) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Compte placé à la corbeille (30 j de rétention)");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const archiveUser = async (u) => {
    if (!window.confirm(`Archiver ${u.name} ? Son accès sera désactivé mais ses données restent intactes.`)) return;
    try {
      await api.post(`/users/${u.id}/archive`);
      toast.success("Compte archivé");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const restoreUser = async (u) => {
    try {
      await api.post(`/users/${u.id}/restore`);
      toast.success("Compte restauré et réactivé");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const permanentDelete = async (u) => {
    if (!window.confirm(`Supprimer DÉFINITIVEMENT ${u.name} et toutes ses données ? Action irréversible.`)) return;
    try {
      await api.delete(`/users/${u.id}/permanent`);
      toast.success("Compte supprimé définitivement");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resetPwd = async () => {
    if (!newPwd || newPwd.length < 4) { toast.error("Mot de passe trop court"); return; }
    try {
      await api.post(`/users/${pwdUser.id}/reset-password`, { new_password: newPwd });
      toast.success(`Mot de passe de ${pwdUser.name} mis à jour`);
      setPwdUser(null);
      setNewPwd("");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="page-users">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-tiny">Équipe</p>
          <h1 className="font-display text-3xl sm:text-4xl text-[#1A2B27] mt-1">Comptes utilisateurs</h1>
          <p className="text-[#6b7f79] mt-1">Gère les accès de l'équipe et des étudiants.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canManageStudents && (
            <Button onClick={openCreate} data-testid="user-create-button" className="rounded-full bg-[#2D5951] hover:bg-[#1F423C] shadow-md">
              <UserPlus className="w-4 h-4 mr-2" /> Ajouter{!isAdmin ? " un étudiant" : ""}
            </Button>
          )}
        </div>
      </div>

      <div className="flex gap-2">
        {(isAdmin ? [
          { v: "active", label: "Actifs" },
          { v: "archived", label: "Archivés" },
          { v: "deleted", label: "Corbeille (30j)" },
        ] : [
          { v: "active", label: "Actifs" },
        ]).map((t) => (
          <Button
            key={t.v}
            variant={tab === t.v ? "default" : "outline"}
            size="sm"
            onClick={() => setTab(t.v)}
            data-testid={`users-tab-${t.v}`}
            className={`rounded-full ${tab === t.v ? "bg-[#2D5951] hover:bg-[#1F423C]" : "border-[#DDE7E4] text-[#6b7f79]"}`}
          >
            {t.label}
          </Button>
        ))}
      </div>

      {(() => {
        const groups = [
          { role: "admin", label: "Administrateurs" },
          { role: "chef", label: "Chefs d'atelier" },
          { role: "student", label: "Étudiants" },
        ];
        return (
          <div className="space-y-6">
            {groups.map((g) => {
              const list = users.filter((u) => u.role === g.role);
              if (list.length === 0) return null;
              return (
                <section key={g.role} data-testid={`users-group-${g.role}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <h2 className="font-display text-lg text-[#1A2B27]">{g.label}</h2>
                    <Badge variant="outline" className="border-[#DDE7E4] text-[#6b7f79] rounded-full text-[10px]">
                      {list.length}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {list.map((u) => (
        <Card key={u.id} className={`card-soft border-0 transition-all hover:-translate-y-0.5 ${!u.active ? "opacity-60" : ""}`} data-testid={`user-card-${u.id}`}>
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <div className={`w-11 h-11 rounded-full flex items-center justify-center font-display text-lg shrink-0 ${u.role === "admin" ? "bg-[#3E6E64] text-white" : u.role === "chef" ? "bg-[#2D5951] text-white" : "bg-[#B8C7C2] text-[#1A2B27]"}`}>
                {u.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#1A2B27] truncate">{u.name}</div>
                <div className="text-xs text-[#94ADA7] truncate">{u.email}</div>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="outline" className="border-[#DDE7E4] text-[#6b7f79] rounded-full text-[10px]">
                    {ROLE_LABEL[u.role]}
                  </Badge>
                  {!u.active && <Badge variant="destructive" className="rounded-full text-[10px]">Inactif</Badge>}
                </div>
                {u.quota_year_hours && <p className="text-[10px] text-[#94ADA7] mt-1">Quota : {u.quota_year_hours} h/an</p>}
                {u.deleted_at && (
                  <p className="text-[10px] text-[#3E6E64] mt-1">
                    🗑 À la corbeille depuis le {new Date(u.deleted_at).toLocaleDateString("fr-BE")} — purge auto J+30
                  </p>
                )}
                {u.archived_at && !u.deleted_at && (
                  <p className="text-[10px] text-[#6b7f79] mt-1">📦 Archivé le {new Date(u.archived_at).toLocaleDateString("fr-BE")}</p>
                )}
              </div>
            </div>
            {(isAdmin || (canManageStudents && u.role === "student")) && (
              <div className="flex flex-wrap gap-1 mt-4">
                {tab === "active" && (
                  <>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid={`user-edit-${u.id}`} className="rounded-full text-[#6b7f79]">
                      <Pencil className="w-4 h-4 mr-1" /> Modifier
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => downloadProfilePdf(u)} data-testid={`user-pdf-${u.id}`} className="rounded-full text-[#6b7f79]" title="Télécharger la fiche PDF">
                      <FileText className="w-4 h-4 mr-1" /> Fiche
                    </Button>
                    {isAdmin && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => setPwdUser(u)} data-testid={`user-reset-${u.id}`} className="rounded-full text-[#6b7f79]">
                          <KeyRound className="w-4 h-4 mr-1" /> Mdp
                        </Button>
                        {u.id !== me.id && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => archiveUser(u)} data-testid={`user-archive-${u.id}`} className="rounded-full text-[#6b7f79]">
                              <Archive className="w-4 h-4 mr-1" /> Archiver
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => deleteUser(u)} data-testid={`user-delete-${u.id}`} className="rounded-full text-[#3E6E64] ml-auto">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
                {tab === "archived" && isAdmin && (
                  <>
                    <Button variant="ghost" size="sm" onClick={() => restoreUser(u)} data-testid={`user-restore-${u.id}`} className="rounded-full text-[#388E3C]">
                      <ArchiveRestore className="w-4 h-4 mr-1" /> Réactiver
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteUser(u)} data-testid={`user-trash-${u.id}`} className="rounded-full text-[#3E6E64] ml-auto">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                )}
                {tab === "deleted" && isAdmin && (
                  <>
                    <Button variant="ghost" size="sm" onClick={() => restoreUser(u)} data-testid={`user-restore-${u.id}`} className="rounded-full text-[#388E3C]">
                      <RotateCcw className="w-4 h-4 mr-1" /> Restaurer
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => permanentDelete(u)} data-testid={`user-permanent-${u.id}`} className="rounded-full text-[#C62828] ml-auto">
                      <Trash2 className="w-4 h-4 mr-1" /> Suppr. définitive
                    </Button>
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>
                    ))}
                  </div>
                </section>
              );
            })}
            {users.length === 0 && (
              <div className="text-sm text-[#94ADA7] italic p-6 text-center">Aucun compte dans cette liste.</div>
            )}
          </div>
        );
      })()}

      {/* Dialog edit/create */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          data-testid="user-edit-dialog"
          className="max-h-[90vh] flex flex-col"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1A2B27]">
              {editing?.id ? "Modifier le compte" : "Ajouter un compte"}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 flex-1 overflow-y-auto min-h-0 pr-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="label-tiny">Prénom</Label>
                  <Input
                    value={editing.first_name || ""}
                    onChange={(e) => setEditing({ ...editing, first_name: e.target.value })}
                    data-testid="user-edit-first-name"
                    className="mt-1 h-11 rounded-xl border-[#DDE7E4]"
                    placeholder="Antoine"
                  />
                </div>
                <div>
                  <Label className="label-tiny">Nom</Label>
                  <Input
                    value={editing.last_name || ""}
                    onChange={(e) => setEditing({ ...editing, last_name: e.target.value })}
                    data-testid="user-edit-last-name"
                    className="mt-1 h-11 rounded-xl border-[#DDE7E4]"
                    placeholder="d'Aspremont"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="label-tiny">Email</Label>
                  <Input type="email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} data-testid="user-edit-email" className="mt-1 h-11 rounded-xl border-[#DDE7E4]" placeholder="prenom@lacantinedegeraldine.be" />
                </div>
                <div>
                  <Label className="label-tiny">Téléphone <span className="opacity-60">(facultatif)</span></Label>
                  <Input type="tel" value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} data-testid="user-edit-phone" className="mt-1 h-11 rounded-xl border-[#DDE7E4]" placeholder="+32 470 12 34 56" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-tiny">Rôle</Label>
                  <Select
                    value={editing.role}
                    onValueChange={(v) => setEditing({ ...editing, role: v })}
                    disabled={!isAdmin}
                  >
                    <SelectTrigger data-testid="user-edit-role" className="mt-1 h-11 rounded-xl border-[#DDE7E4]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {isAdmin && <SelectItem value="admin">Admin</SelectItem>}
                      {isAdmin && <SelectItem value="chef">Chef d'atelier</SelectItem>}
                      <SelectItem value="student">Étudiant</SelectItem>
                    </SelectContent>
                  </Select>
                  {!isAdmin && (
                    <p className="text-[10px] text-[#94ADA7] mt-1">Un chef d'atelier crée uniquement des comptes étudiants.</p>
                  )}
                </div>
                <div>
                  <Label className="label-tiny">Quota / an (h)</Label>
                  <Input type="number" placeholder="650 par défaut" value={editing.quota_year_hours || ""} onChange={(e) => setEditing({ ...editing, quota_year_hours: e.target.value })} data-testid="user-edit-quota" className="mt-1 h-11 rounded-xl border-[#DDE7E4]" />
                </div>
              </div>
              {editing.id && (
                <div className="flex items-center gap-2">
                  <Switch checked={!!editing.active} onCheckedChange={(c) => setEditing({ ...editing, active: c })} data-testid="user-edit-active" />
                  <span className="text-sm">Compte actif</span>
                </div>
              )}

              {/* Profil étendu — facultatif */}
              <details className="border border-[#DDE7E4] rounded-xl p-3" data-testid="user-extended-profile">
                <summary className="cursor-pointer text-sm font-semibold text-[#2D5951] select-none">
                  Profil étendu (adresse, banque, contact d'urgence) — facultatif
                </summary>
                <div className="mt-3 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="label-tiny">Date de naissance</Label>
                      <Input type="date" value={editing.date_of_birth || ""} onChange={(e) => setEditing({ ...editing, date_of_birth: e.target.value })} data-testid="user-edit-dob" className="mt-1 h-11 rounded-xl border-[#DDE7E4]" />
                    </div>
                    <div>
                      <Label className="label-tiny">N° registre national</Label>
                      <Input value={editing.national_register || ""} onChange={(e) => setEditing({ ...editing, national_register: e.target.value })} placeholder="00.00.00-000.00" data-testid="user-edit-nrn" className="mt-1 h-11 rounded-xl border-[#DDE7E4]" />
                    </div>
                  </div>
                  <div>
                    <Label className="label-tiny">Rue et numéro</Label>
                    <Input value={editing.address_street || ""} onChange={(e) => setEditing({ ...editing, address_street: e.target.value })} data-testid="user-edit-street" className="mt-1 h-11 rounded-xl border-[#DDE7E4]" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="label-tiny">Code postal</Label>
                      <Input value={editing.address_postal_code || ""} onChange={(e) => setEditing({ ...editing, address_postal_code: e.target.value })} data-testid="user-edit-cp" className="mt-1 h-11 rounded-xl border-[#DDE7E4]" />
                    </div>
                    <div>
                      <Label className="label-tiny">Ville</Label>
                      <Input value={editing.address_city || ""} onChange={(e) => setEditing({ ...editing, address_city: e.target.value })} data-testid="user-edit-city" className="mt-1 h-11 rounded-xl border-[#DDE7E4]" />
                    </div>
                    <div>
                      <Label className="label-tiny">Pays</Label>
                      <Input value={editing.address_country || ""} onChange={(e) => setEditing({ ...editing, address_country: e.target.value })} data-testid="user-edit-country" className="mt-1 h-11 rounded-xl border-[#DDE7E4]" />
                    </div>
                  </div>
                  <div>
                    <Label className="label-tiny">IBAN</Label>
                    <Input value={editing.iban || ""} onChange={(e) => setEditing({ ...editing, iban: e.target.value })} placeholder="BE68 5390 0754 7034" data-testid="user-edit-iban" className="mt-1 h-11 rounded-xl border-[#DDE7E4] font-mono" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="label-tiny">Urgence — Nom</Label>
                      <Input value={editing.emergency_contact_name || ""} onChange={(e) => setEditing({ ...editing, emergency_contact_name: e.target.value })} data-testid="user-edit-ec-name" className="mt-1 h-11 rounded-xl border-[#DDE7E4]" />
                    </div>
                    <div>
                      <Label className="label-tiny">Urgence — Tél.</Label>
                      <Input type="tel" value={editing.emergency_contact_phone || ""} onChange={(e) => setEditing({ ...editing, emergency_contact_phone: e.target.value })} data-testid="user-edit-ec-phone" className="mt-1 h-11 rounded-xl border-[#DDE7E4]" />
                    </div>
                    <div>
                      <Label className="label-tiny">Urgence — Lien</Label>
                      <Input value={editing.emergency_contact_relation || ""} onChange={(e) => setEditing({ ...editing, emergency_contact_relation: e.target.value })} placeholder="Parent, conjoint…" data-testid="user-edit-ec-relation" className="mt-1 h-11 rounded-xl border-[#DDE7E4]" />
                    </div>
                  </div>
                </div>
              </details>
            </div>
          )}
          <DialogFooter className="shrink-0 pt-2 border-t border-[#DDE7E4]">
            <Button onClick={save} data-testid="user-edit-save" className="rounded-full bg-[#2D5951] hover:bg-[#1F423C]">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog reset password */}
      <Dialog open={!!pwdUser} onOpenChange={(o) => !o && setPwdUser(null)}>
        <DialogContent data-testid="user-reset-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1A2B27]">Réinitialiser le mot de passe</DialogTitle>
          </DialogHeader>
          {pwdUser && (
            <div className="space-y-3">
              <p className="text-sm text-[#6b7f79]">Pour <b>{pwdUser.name}</b> ({pwdUser.email})</p>
              <Input type="text" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} placeholder="Nouveau mot de passe" data-testid="user-reset-input" className="h-11 rounded-xl border-[#DDE7E4]" />
            </div>
          )}
          <DialogFooter>
            <Button onClick={resetPwd} data-testid="user-reset-save" className="rounded-full bg-[#2D5951] hover:bg-[#1F423C]">
              Mettre à jour
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
